import { useMailUserList } from "../../api/api-hooks";
import { createColumnHelper } from "@tanstack/react-table";
import ReactTanstackTableUi from "../../common/ReactTanstackTableUi";
import { usePagination } from "../hooks/usePagination";
import useCommonStore from "../../store/CommonStore";
import { getDateRangeFromSelectedValue } from "../../common/Constants";
import { useMemo, useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { useAuth } from "../../lib/AuthContext";

interface ConfirmationModalProps {
    onConfirm: () => void;
    onCancel: () => void;
    mailData: any;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    onConfirm,
    onCancel,
    mailData,
}) => {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded shadow-md w-[800px]">
                <h2 className="text-lg font-semibold mb-4">Delete Mail Entry</h2>
                <div className="flex flex-col gap-2 my-4">
                    <p>
                        This action will permanently remove the selected mail entry from your records.
                        It cannot be undone.
                    </p>
                    <div
                        className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50"
                        aria-live="polite"
                    >
                        <div className="grid grid-cols-[140px_1fr] gap-y-1.5 gap-x-2.5 text-sm">
                            <div className="text-gray-500">Name</div>
                            <div>{mailData.name}</div>

                            <div className="text-gray-500">Email</div>
                            <div>{mailData.email}</div>

                            <div className="text-gray-500">Mail Type</div>
                            <div>{mailData.mail_type}</div>

                            <div className="text-gray-500">Sent On</div>
                            <div>{new Date(mailData?.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric"
                            }) || "--"}</div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gray-200 mt-4"></div>
                </div>
                {/* <div className="flex justify-end space-x-4">
                    <button
                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-4 py-2 bg-green-600 text-white rounded"
                        onClick={onConfirm}
                    >
                        OK
                    </button>
                </div> */}

                <div className="px-4.5 pt-3 pb-4.5 flex justify-end gap-2.5">
                    {/* Cancel Button */}
                    <button
                        id="cancel-btn"
                        onClick={onCancel}
                        autoFocus
                        className="border border-gray-300 bg-white px-3.5 py-2.5 rounded-lg font-semibold text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 transition-colors"
                    >
                        Cancel
                    </button>

                    {/* Delete Button */}
                    <button
                        onClick={onConfirm}
                        className="bg-rose-600 border border-rose-600 text-white px-3.5 py-2.5 rounded-lg font-semibold hover:bg-rose-700 hover:border-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:ring-offset-2 transition-colors inline-flex items-center gap-2"
                    >
                        🗑️ <span>Delete</span>
                    </button>
                </div>
            </div>
        </div>
    );
};


function prepareColumns(columnHelper: any, handleDeleteClick: any, setMailData: any) {
    return [
        columnHelper.accessor((row: any) => row.name, {
            id: "name",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base truncate w-full max-w-xs">
                            {original?.name || "--"}
                        </div>
                    </div>
                );
            },
            header: () => <div>Name</div>,
        }),
        columnHelper.accessor((row: any) => row.email, {
            id: "email",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base truncate w-full max-w-xs">
                            {original?.email || "--"}
                        </div>
                    </div>
                );
            },
            header: () => <div>Email</div>,
        }),
        columnHelper.accessor((row: any) => row.mail_type, {
            id: "mail_type",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base max-w-xs">
                            {original?.mail_type || "--"}
                        </div>
                    </div>
                );
            },
            header: () => <div>Mail Type</div>,
        }),
        columnHelper.accessor((row: any) => row.created_at, {
            id: "created_at",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base max-w-xs">
                            {new Date(original?.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric"
                            }) || "--"}
                        </div>
                    </div>
                );
            },
            header: () => <div>Send</div>,
        }),
        columnHelper.accessor((row: any) => row.is_opened, {
            id: "is_opened",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base max-w-xs">
                            {original.is_opened ? "Yes" : "No"}
                        </div>
                    </div>
                );
            },
            header: () => <div>Opened</div>,
        }),
        columnHelper.accessor((row: any) => row.click_count, {
            id: "click_count",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base max-w-xs">
                            {original.click_count ? `Yes - ${original.click_count}` : "No"}
                        </div>
                    </div>
                );
            },
            header: () => <div>Clicked</div>,
        }),
        columnHelper.accessor((row: any) => row.registered, {
            id: "registered",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base max-w-xs">
                            {original.registered ? "Yes" : "No"}
                        </div>
                    </div>
                );
            },
            header: () => <div>Signup</div>,
        }),
        columnHelper.accessor((row: any) => row.last_session, {
            id: "last_session",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className=" flex flex-col gap-1">
                        <div className="font-semibold text-gray-800 text-base max-w-xs">
                            {original?.last_session ? new Date(original?.last_session).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric"
                            }) : '--'}
                        </div>
                    </div>
                );
            },
            header: () => <div>Last Intraction</div>,
        }),
        columnHelper.accessor((row: any) => row.actions, {
            id: "Actions",
            cell: (info: any) => {
                const row = info.row;
                const original = row?.original;

                return (
                    <div className="flex items-center space-x-4">
                        {/* Delete Icon */}
                        <FaTrashAlt
                            className="text-red-500 cursor-pointer"
                            onClick={() => {
                                // handleActions("delete", original?.research_id)
                                handleDeleteClick()
                                setMailData(original)
                            }}
                            title="Delete"
                        />
                    </div>
                );
            },
            header: () => <div>Actions</div>,
        }),
    ];
}

function UserListTable() {
    const { selectedValue, mailStatusFilter } = useCommonStore();
    const [showModel, setShowModel] = useState(false);
    const [mailData, setMailData] = useState({} as any);

    const { hasAuthenticated } = useAuth();

    const apiURL = process.env.REACT_APP_API_URL

    const dateRange = getDateRangeFromSelectedValue(selectedValue);

    const { startDate, endDate } = dateRange!;

    const { pageIndex, pageSize, getPaginationInfo } = usePagination();
    //   const { data: feedback, isLoading: isFeedbackLoading } = useFeedback();

    const { data: user_list, isLoading: isUserListLoading, refetch: refetchUserList } = useMailUserList(
        pageIndex + 1,
        pageSize,
        startDate,
        endDate,
        mailStatusFilter.value,
    );

    const apiData = useMemo(() => {
        return Array.isArray(user_list?.items) ? user_list?.items : [];
    }, [user_list]);

    const handleDeleteClick = () => {
        setShowModel(!showModel)
    }

    const columnHelper = createColumnHelper();

    const columns = prepareColumns(columnHelper, handleDeleteClick, setMailData);

    const paginationInfo = getPaginationInfo(user_list);

    const deleteMailEntry = async (id?: string) => {
        if (!id) return;
        if (!hasAuthenticated) return;

        try {
            const res = await fetch(`${apiURL}/mail-send-user/${id}`, {
                method: 'DELETE',
                headers: {
                    Accept: "application/json",
                    Token: hasAuthenticated, // your JWT or token string
                },
            });

            if (!res.ok) {
                throw new Error(`Failed to delete entry (status ${res.status})`);
            }

            // If your API returns JSON after delete
            await res.json();

            // Refresh the list after deletion
            await refetchUserList();

        } catch (error) {
            console.error("Delete error:", error);
        }
    };

    return (
        <>
            <div className="md:p-10 p-3">
                <ReactTanstackTableUi
                    data={apiData}
                    columns={columns}
                    noDataMessage="Email Invites Sent"
                    tableTitle="Email Invites Sent"
                    disableRowHeight={true}
                    pagination={true}
                    externalPagination={paginationInfo}
                    isLoading={isUserListLoading}
                    emailInvites={true}
                />
            </div>
            {showModel && (
                <ConfirmationModal
                    onCancel={() => {
                        handleDeleteClick()
                    }}
                    onConfirm={() => {
                        deleteMailEntry(mailData?.id);
                        setShowModel(false);
                        setMailData({});
                    }}
                    mailData={mailData}
                />
            )}
        </>
    );
}

function SendUserMailList() {
    return (
        <div className="flex flex-col gap-6 ">
            <div>
                <UserListTable />
            </div>
        </div>
    );
}

export default SendUserMailList;
